import { ReactNode } from 'react';
import cx from 'classnames';
import { CheckboxFinalFormAdapter } from 'components/Checkbox/Checkbox';
import { NewMediaUploadAdapter } from 'components/NewMediaUploader/NewMediaUploader';
import PropTypes from 'prop-types';
import { HOME_OWNER_PAPER_WORK_STRINGS as STRINGS } from 'strings/homeowner-paperwork.strings';

type SkippableMediaValue = {
  later?: boolean;
  mediaToDisplay?: unknown[];
};

type SkippableMediaUploaderProps = {
  input: {
    value: SkippableMediaValue;
    onChange: (value: SkippableMediaValue) => void;
  };
  toolTip?: ReactNode;
  customCheckboxClassname?: string;
  checkboxProps?: Record<string, unknown>;
  hideCheckboxWithValue?: boolean;
  // Stands for the props `...rest` forwards to `NewMediaUploadAdapter` — the
  // `fileType` and layout props the `propTypes` block below only partly lists.
  [key: string]: unknown;
};

function showCheckbox(
  hideCheckboxWithValue: boolean | undefined,
  length: number | undefined,
) {
  if (hideCheckboxWithValue && length) {
    return false;
  }

  return true;
}

export function validateSkippableMedia(value?: SkippableMediaValue) {
  if (value?.later === true) {
    return undefined;
  }

  if (value?.mediaToDisplay?.length) {
    return undefined;
  }

  return true;
}

function SkippableMediaUploader({
  input,
  toolTip,
  customCheckboxClassname,
  checkboxProps,
  hideCheckboxWithValue,
  ...rest
}: SkippableMediaUploaderProps) {
  const { value, onChange } = input;
  const onCheckboxChange = (checkboxValue: boolean) => {
    const newValue = {
      ...value,
      later: checkboxValue,
    };
    onChange(newValue);
  };

  return (
    <div>
      <NewMediaUploadAdapter {...rest} input={input} />
      {showCheckbox(hideCheckboxWithValue, value?.mediaToDisplay?.length) && (
        <div
          className={cx(
            'pt-sm text-dark-gray flex items-center justify-end font-semibold text-body',
            customCheckboxClassname,
          )}
        >
          <CheckboxFinalFormAdapter
            useOnChangeFormToHandle
            alignWithField={false}
            input={{
              value: value.later,
              onChange: onCheckboxChange,
            }}
            {...checkboxProps}
          />
          {toolTip}
        </div>
      )}
    </div>
  );
}

SkippableMediaUploader.propTypes = {
  fileType: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func,
  hideCheckboxWithValue: PropTypes.bool,
  checkboxProps: PropTypes.object,
  customCheckboxClassname: PropTypes.string,
  toolTip: PropTypes.node,
  input: PropTypes.object.isRequired,
};

SkippableMediaUploader.defaultProps = {
  value: {},
  hideCheckboxWithValue: true,
  checkboxProps: {
    label: STRINGS['around_the_property.uploadHoa.donthave'],
  },
  toolTip: null,
};

export function SkippableMediaUploaderAdapter({
  input,
  meta,
  ...rest
}: SkippableMediaUploaderProps & { meta?: unknown }) {
  return <SkippableMediaUploader {...rest} meta={meta} input={input} />;
}

SkippableMediaUploaderAdapter.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
};
